<script>
/**
 * The MatchTeamGameNote view works for the notebook for the special
 * event happened in the game. 
 */

//--------------------------------------------------
// imported classes
//--------------------------------------------------
import { Field } from 'vant';
import { DateUtil } from '@/utils'
import { getPassport } from '@/libs/passport'
import GameSummary from './game-summary.vue'
import { getGameDetail, getGameSpecialRemark, updateGameSpecialRemark } from '@/api/match-team-game'

//--------------------------------------------------
// main script
//--------------------------------------------------
export default {
    //--------------------------------------------------
	// component name
	//--------------------------------------------------
	name: 'match-team-game-note',

    //--------------------------------------------------
    // sub-components
    //--------------------------------------------------
	components: {
        /* The game summary */
        'game-summary': GameSummary,
        'van-field': Field
	},

    //--------------------------------------------------
    // component data
    //--------------------------------------------------
    data() {
        return {
            /* The game info */
            gameInfo: {
                /* The title string */
                title: null,
                /* The time string */
                time: null,
                /* The home team id */
                homeTeamId: null,
                /* The home team name */
                homeTeamName: null,
                /* The home team badge */
                homeTeamBadge: null,
                /* The home team score */
                homeTeamScore: null,
                /* The away team id */
                awayTeamId: null,
                /* The away team name */
                awayTeamName: null,
                /* The away team badge */
                awayTeamBadge: null,
                /* The away team score */
                awayTeamScore: null,
            },
            /* The note content for the game */
            content: null
        }
    },

    methods: {
        //--------------------------------------------------
        // request method
        //--------------------------------------------------
        /**
         * @request
         * Query the game info and set the bindable data model.
         * @param gameId The game Id string.
         * @return The reponse object.
         */
        queryGameInfo() {
            const routerParams = this.$route.params
            const params = {
                gameId: routerParams.gameId,
                eventMode: 0,
                simple: 0
            }
            getGameDetail(params).then(response => {
                console.log("get game detail response", response)
                const data = response.data
                // set the game info data
                let title = data.game.matchName
                if (data.game.gameType) {
                    title += ' | ' + data.game.gameType
                }
                if (data.game.roundDescription) {
                    title += ' | ' + data.game.roundDescription
                }
                this.gameInfo.title = title
                const weekDay = DateUtil.parseDateTime(data.game.beginDate, '{a}')
                console.log('weekDay', weekDay)
                const startTime = DateUtil.parseDateTime(data.game.beginDate, '{y}-{m}-{d} {h}:{i}')
                const endTime = DateUtil.parseDateTime(data.game.endDate, '{h}:{i}')
                this.gameInfo.time = '周' + weekDay + ' ' + startTime + '~' + endTime
                this.gameInfo.homeTeamId = data.game.homeTeamId
                this.gameInfo.homeTeamName = data.game.homeTeamName
                this.gameInfo.homeTeamBadge = data.game.homeTeamBadge
                if (data.game.homeScore > -1) {
                    this.gameInfo.homeTeamScore = data.game.homeScore
                }    
                this.gameInfo.awayTeamId = data.game.awayTeamId
                this.gameInfo.awayTeamName = data.game.awayTeamName
                this.gameInfo.awayTeamBadge = data.game.awayTeamBadge
                if (data.game.awayScore > -1) {
                    this.gameInfo.awayTeamScore = data.game.awayScore
                } 
                // query the game note by sequence
                this.queryGameNote()
            })
        },
        /**
         * @request
         * Query the game note and set the bindable data model.
         * @param gameId The game Id string.
         * @return The reponse object.
         */
        queryGameNote() {
            const routerParams = this.$route.params
            const params = {
                gameId: routerParams.gameId
            }
            getGameSpecialRemark(params).then(response => {
                console.log('get game special remark resposne', response.data)
                const data = response.data
                if (data.specialRemark) {
                    this.content = data.specialRemark
                }
            })
        },
        /**
         * @request
         * Update the game note via the bindable data model.
         * @param gameId The game Id string.
         * @param remark The note content string.
         * @return The reponse object.
         */
        updateGameNote() {
            const routerParams = this.$route.params
            const params = {
                gameId: routerParams.gameId
            }
            const data = {
                remark: this.content
            }
            updateGameSpecialRemark(params, data).then(response => {
                console.log('get game special remark resposne', response.data)
                // back to the landing page
                this.$router.go(-1)
            })
        },
        //--------------------------------------------------
        // handler method
        //--------------------------------------------------
        /**
         * @handler
         * Handle the save button click event.
         */
        onSaveBtnClick() {
            this.updateGameNote()
        }
    },

    /**
     * @created
     * Set the data model via the match id, game id and passport.
     * Query the game info data according to the params and cookies.
     * All parameters are required. 
     * Use getPassport to ensure the authentication status.
     */
	created() {
        // validate the passport and prepair the one
        getPassport().then(res => {
            console.log('getPassport', res)
            if (res) {
                this.queryGameInfo()
            }
        })
	}
}
</script>

<template>
    <!-- component -->
    <div class="match-team-game-note">
        <!-- head start -->
        <div class="head">
            <game-summary
                :title="gameInfo.title"
                :time="gameInfo.time"
                :homeTeamName="gameInfo.homeTeamName"
                :homeTeamBadge="gameInfo.homeTeamBadge"
                :homeTeamScore="gameInfo.homeTeamScore"
                :awayTeamName="gameInfo.awayTeamName"
                :awayTeamBadge="gameInfo.awayTeamBadge"
                :awayTeamScore="gameInfo.awayTeamScore">
            </game-summary>
        </div>
        <!-- head end -->
        <!-- body start -->
        <div class="body">
            <div class="note-wrapper">
                <div class="note">
                    <span class="label">特别备注</span>
                </div>
                <div class="content">
                    <van-field class="content-field" 
                        type="textarea"
                        rows="12"
                        v-model="content" 
                        placeholder="请输入需要上报的比赛特殊备注">
                    </van-field>
                </div>
            </div>
        </div>
        <!-- body end -->
        <!-- footer start -->
        <div class="footer">
            <div class="fixed-layer">
                <div class="btn save-btn" @click="onSaveBtnClick">
                    <span class="label">保存</span>
                </div>
            </div>
        </div>
        <!-- footer end -->
    </div>
</template>

<style scoped>
/**
 * @component
 */
.match-team-game-note {
    background: #F7F8FA;
    min-height: 100vh;
}
/**
 * @body
 */
.note-wrapper {
    margin: 22px 18px;
}
.note {
    margin-left: 14px;
}
.note .label {
    font-family: Source Han Sans;
    font-size: 12px;
    font-weight: normal;
    line-height: normal;
    letter-spacing: normal;
    color: #979797;
}
.content {
    margin-top: 6px;
}
.content-field {
    height: 345px;
    border-radius: 5px;
    background: #FFFFFF;
    font-family: Source Han Sans;
    font-size: 15px;
    font-weight: normal;
    line-height: 27px;
    letter-spacing: 0.25px;
    color: #000000;
}
/**
 * @footer
 */
.footer {
    height: 65px;
}
.fixed-layer {
    height: 65px;
    position: fixed;
    width: 100%;
    bottom: 0;
}
.btn {
    height: 45px;
    /**
     * avoid the compatiable issue on the app
     * NOT: align-content: center;
     * BUT: line-height: 1;
     * It seems that the justify-content attribute works well. 
     */
    /*
    text-align: center;
    display: inline-block;
    line-height: 45px;
    */
    display: flex;
    align-items: center;
    justify-content: center;
}
.save-btn {
    margin: 10px 15px;
    width: 345px;
    border-radius: 6px;
    background: #0FAC7F;
}
.save-btn .label {
    font-family: Source Han Sans;
    font-size: 15px;
    font-weight: 500;
    letter-spacing: normal;
    color: #FFFFFF;
}
</style>